import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { changePassword, patchMe, fetchMe } from '@/api/auth';
import { dids } from '@/api/dids';
import { ivrTrees } from '@/api/ivrTrees';
import { extensions } from '@/api/extensions';
import { ringGroups } from '@/api/ringGroups';
import { voicemails } from '@/api/voicemails';
import { timeFrames, formatTimeFrameRef } from '@/api/timeFrames';
import { users } from '@/api/users';
import type { DidRow, RoutingKind } from '@/api/dids';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { formatE164 } from '@/app/portal/dids/utils';
import { Hash, Phone, Pencil, Plus, Unlink, UserPlus, Users as UsersIcon } from 'lucide-react';
import { useCustomerContext, useEffectiveCustomerId } from '@/lib/customer-context-store';
import { RoleBadge } from '@/components/RoleBadge';

const schema = z.object({
  oldPassword: z.string().min(1, 'Required'),
  newPassword: z.string().min(12, 'At least 12 characters'),
  confirm: z.string().min(1, 'Please confirm'),
}).refine((v) => v.newPassword === v.confirm, { message: 'Passwords do not match', path: ['confirm'] });
type V = z.infer<typeof schema>;

// Roles that see the Phone Numbers card on /account. Customer roles
// (office_manager, standard_user, limited_user) see their own DIDs;
// super_admin + reseller_owner also see it so they can sanity-check
// what their customers / tenants are looking at without leaving the
// account page. agent is included for symmetry — they may need to see
// DIDs assigned to their workspace.
const SHOW_PHONE_NUMBERS_ROLES = [
  'super_admin', 'reseller_owner', 'agent',
  'office_manager', 'standard_user', 'limited_user',
];

export function Account() {
  const me = useCurrentUser();
  const setUser = useAuth((s) => s.setUser);
  const [result, setResult] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<V>({ resolver: zodResolver(schema) });
  // Sam's rule (2026-05-14, clarified 2026-05-15):
  //   • Flat /account = the LOGGED-IN user's own account. NO customer-scoped
  //     data, even when the Zustand customer-context is still set from a
  //     previous drill-in. Admin / reseller manage their own DIDs from the
  //     dedicated /dids tab, not from /account.
  //   • /customer/:cn/account or /reseller/:rn/customer/:cn/account = viewing
  //     THAT customer's account — show their phone numbers card.
  //
  // We key off the URL param `customerNumber` (set by ScopedCustomerLayout)
  // rather than Zustand context, because Zustand persists across page
  // navigation and isn't trustworthy as a "am I on the scoped URL" signal.
  const { customerNumber } = useParams<{ customerNumber?: string }>();
  const isScopedView = customerNumber != null;
  // Customer roles (office_manager / standard_user / limited_user / agent)
  // have an implicit single-customer scope — they always see their own
  // DIDs on /account. Only super_admin and reseller_owner are gated on
  // being inside a scoped URL.
  const isAdminOrReseller =
    me.role === 'super_admin' || me.role === 'reseller_owner';
  const showPhoneNumbers =
    SHOW_PHONE_NUMBERS_ROLES.includes(me.role)
    && (!isAdminOrReseller || isScopedView);

  // Office managers manage their account's users from HERE instead of a
  // dedicated Users sidebar tab (Sam 2026-07-02). The /users/:id and
  // /users/new pages stay reachable — this card links into them.
  const showUsersCard = me.role === 'office_manager';

  // Email change state
  const [emailEdit, setEmailEdit] = useState('');
  const [emailEditOpen, setEmailEditOpen] = useState(false);
  const [emailResult, setEmailResult] = useState<string | null>(null);
  const [emailSaving, setEmailSaving] = useState(false);

  return (
    <>
      <PageHeader eyebrow="Account" title="My account" description={me.email} />
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="text-right sm:text-left break-all">
                {me.email}
                {!emailEditOpen && (
                  <button
                    type="button"
                    className="ml-2 text-xs text-primary underline-offset-2 hover:underline"
                    onClick={() => { setEmailEdit(me.email); setEmailEditOpen(true); setEmailResult(null); }}
                  >
                    Change
                  </button>
                )}
                {emailEditOpen && (
                  <div className="mt-2 flex flex-col gap-2">
                    <Input
                      id="emailEdit"
                      type="email"
                      value={emailEdit}
                      onChange={(e) => setEmailEdit(e.target.value)}
                      placeholder="new@example.com"
                      className="h-7 text-xs"
                    />
                    {emailResult && (
                      <p role="alert" className="text-xs text-destructive">{emailResult}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        disabled={!emailEdit.trim() || emailSaving}
                        onClick={async () => {
                          setEmailResult(null);
                          setEmailSaving(true);
                          try {
                            const resp = await patchMe({ email: emailEdit.toLowerCase().trim() });
                            setUser(resp.user);
                            setEmailEditOpen(false);
                            setEmailEdit('');
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : 'update_failed';
                            setEmailResult(msg === 'email_taken' ? 'Email already in use.' : msg);
                          } finally {
                            setEmailSaving(false);
                          }
                        }}
                      >
                        {emailSaving ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs px-2"
                        onClick={() => { setEmailEditOpen(false); setEmailEdit(''); setEmailResult(null); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </dd>

              <dt className="text-muted-foreground">Display name</dt>
              <dd className="text-right sm:text-left">{me.display_name ?? <span className="text-muted-foreground">—</span>}</dd>

              <dt className="text-muted-foreground">Role</dt>
              <dd className="text-right sm:text-left capitalize">{me.role.replace(/_/g, ' ')}</dd>

              <dt className="text-muted-foreground">MFA</dt>
              <dd className="text-right sm:text-left">
                <Badge variant={me.mfa_enabled ? 'default' : 'secondary'}>
                  {me.mfa_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </dd>
            </dl>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
              MFA enroll/disable flows live under /auth/mfa endpoints and are wired in a follow-up step once we have a recovery flow.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Change password</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              noValidate
              onSubmit={handleSubmit(async (v) => {
                setResult(null);
                try {
                  await changePassword(v.oldPassword, v.newPassword);
                  setResult('Password updated. You will stay logged in on this device; other sessions were revoked.');
                  reset();
                } catch (err) {
                  setResult(err instanceof Error ? err.message : 'Change failed.');
                }
              })}
            >
              <div className="space-y-1">
                <Label htmlFor="oldPassword">Current password</Label>
                <Input id="oldPassword" type="password" autoComplete="current-password" aria-invalid={!!errors.oldPassword} aria-describedby={errors.oldPassword ? 'oldPassword-error' : undefined} {...register('oldPassword')} />
                {errors.oldPassword && <p id="oldPassword-error" role="alert" className="text-xs text-destructive">{errors.oldPassword.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="newPassword">New password</Label>
                <Input id="newPassword" type="password" autoComplete="new-password" aria-invalid={!!errors.newPassword} aria-describedby={errors.newPassword ? 'newPassword-error' : undefined} {...register('newPassword')} />
                {errors.newPassword && <p id="newPassword-error" role="alert" className="text-xs text-destructive">{errors.newPassword.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input id="confirm" type="password" autoComplete="new-password" aria-invalid={!!errors.confirm} aria-describedby={errors.confirm ? 'confirm-error' : undefined} {...register('confirm')} />
                {errors.confirm && <p id="confirm-error" role="alert" className="text-xs text-destructive">{errors.confirm.message}</p>}
              </div>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating…' : 'Update password'}
              </Button>
              {result && <p className="text-sm" role="alert">{result}</p>}
            </form>
          </CardContent>
        </Card>
      </div>

      {showUsersCard && (
        <div className="mt-6">
          <UsersCard />
        </div>
      )}

      {showPhoneNumbers && (
        <div className="mt-6">
          <PhoneNumbersCard />
        </div>
      )}
    </>
  );
}

// ── Users card (office_manager) ──────────────────────────────────────────────
//
// Office managers manage their account's users from the Account page (the
// Users sidebar tab is hidden for them — Sam 2026-07-02). The list is
// implicitly scoped to their own customer by the backend (RLS + tenant
// scope), rows link to the full /users/:id editor, and the Add-user button
// reuses the existing /users/new flow.

function UsersCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => users.list(),
  });
  const rows = data?.rows ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
              Users
            </CardTitle>
            <CardDescription>
              People who can sign in to this account. Select a user to change
              their details, role, or access.
            </CardDescription>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0">
            <Link to="/users/new">
              <UserPlus className="h-4 w-4" /> Add user
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p role="alert" className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load users.'}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No users yet. Add one to give a colleague access to this account.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {rows.map((u) => (
              <li key={u.id}>
                <Link
                  to={`/users/${u.ref_number ?? u.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-secondary/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">
                      {u.display_name ?? u.email}
                    </div>
                    {u.display_name && (
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <RoleBadge role={u.role} />
                    <Badge variant={u.active ? 'default' : 'secondary'}>
                      {u.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── Recording PIN card — REMOVED 2026-05-14 ──────────────────────────────────
//
// Sam asked us to remove this card from the account page: phone-admin
// authentication now lives on per-customer `customer_recording_admins`
// rows registered at /settings/recording-admins (via OTP-callback), not
// on the portal user's account. The old recording-PIN flow predated
// that and was confusing operators who had both. Backend endpoints
// (users.setRecordingPin / removeRecordingPin) are intentionally kept
// for back-compat with any in-flight callers; the dial-in flow no
// longer reads the user.recording_pin column.
//
// All RecordingPinCard / pinSchema / PinForm code was deleted here.

function PhoneNumbersCard() {
  const user = useCurrentUser();
  // Number management is RESELLER-level (Sam 2026-07-02, reversing the
  // 2026-05-14 "office_manager can re-route own DIDs" feature): only
  // super_admin / reseller_owner may re-route, assign, or release numbers.
  // Customer roles — office_manager included — see the list read-only and
  // go through their reseller for any change. The backend enforces the
  // same contract (PATCH /dids is super_admin + reseller_owner only).
  const canManage = ['super_admin', 'reseller_owner'].includes(user.role);

  // When visited via /customer/:cn/account or /reseller/:rn/customer/:cn/account
  // the ScopedCustomerLayout sets a Zustand customer context — use it to
  // scope the DID list to JUST that customer's numbers. On the flat
  // /account (no context) super_admin / reseller_owner see all visible
  // DIDs (per-RLS), matching the legacy behavior; office_manager etc.
  // are always implicitly scoped to their own tenant by RLS regardless.
  const effectiveCustomerId = useEffectiveCustomerId();
  // The entered customer's reseller — used to scope the assignable pool
  // for super_admin (reseller_owner is already scoped by RLS).
  const contextResellerId = useCustomerContext((s) => s.current?.resellerId ?? null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['account-dids', effectiveCustomerId ?? 'all'],
    queryFn: () => dids.list({ limit: 200, customerId: effectiveCustomerId ?? undefined }),
  });
  const rows = data?.rows ?? [];
  const [editTarget, setEditTarget] = useState<DidRow | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<DidRow | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  // Assign needs to know WHICH customer receives the number. SA/RO only
  // reach this card through the scoped /customer/:cn/account URL, so the
  // context id is set whenever canManage is — this is belt-and-braces.
  const canAssign = canManage && Boolean(effectiveCustomerId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              Phone numbers
            </CardTitle>
            <CardDescription>
              {canManage
                ? 'Assign numbers from your pool, click the pencil to change where a number routes, or release one back to the pool.'
                : 'Read-only. Contact your reseller to change where a number routes or to add and remove numbers.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary">{rows.length}</Badge>
            {canAssign && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setAssignOpen(true)}
              >
                <Plus className="h-4 w-4" /> Assign number
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p role="alert" className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load numbers.'}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {canAssign
              ? 'No phone numbers assigned to this account yet. Assign one from your pool to start taking calls.'
              : 'No phone numbers assigned to your account yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {rows.map((row) => (
              <PhoneNumberRow
                key={row.id}
                row={row}
                canManage={canManage}
                onEdit={() => setEditTarget(row)}
                onRelease={() => setReleaseTarget(row)}
              />
            ))}
          </ul>
        )}
      </CardContent>
      {editTarget && (
        <EditDidRoutingDialog
          row={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
      {releaseTarget && (
        <ReleaseNumberDialog
          row={releaseTarget}
          onClose={() => setReleaseTarget(null)}
        />
      )}
      {assignOpen && effectiveCustomerId && (
        <AssignNumberDialog
          customerId={effectiveCustomerId}
          resellerId={contextResellerId}
          onClose={() => setAssignOpen(false)}
        />
      )}
    </Card>
  );
}

function PhoneNumberRow({
  row, canManage, onEdit, onRelease,
}: {
  row: DidRow; canManage: boolean; onEdit: () => void; onRelease: () => void;
}) {
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm tracking-wide">{formatE164(row.e164)}</div>
        <RoutingSummary row={row} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <CoverageTag coverage={row.coverage} />
        {!row.active && (
          <span className="inline-flex items-center rounded border border-dashed border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Inactive
          </span>
        )}
        {canManage && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onEdit}
              aria-label={`Edit routing for ${formatE164(row.e164)}`}
              className="h-8 w-8"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRelease}
              aria-label={`Release ${formatE164(row.e164)} from this account`}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
            >
              <Unlink className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * ReleaseNumberDialog — unassign a DID from this account. The number keeps
 * its reseller and returns to the pool (the backend retains reseller_id on
 * release), so it can be re-assigned to another account later.
 */
function ReleaseNumberDialog({ row, onClose }: { row: DidRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const release = useMutation({
    mutationFn: () => dids.update(row.id, { customer_id: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-dids'] });
      qc.invalidateQueries({ queryKey: ['dids'] });
      toast({
        title: 'Number released',
        description: `${formatE164(row.e164)} was returned to the pool.`,
      });
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not release the number';
      toast({ title: 'Could not release', description: m, variant: 'destructive' });
    },
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Release {formatE164(row.e164)}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Callers will no longer reach this account on this number. The number
          returns to the pool and can be assigned to another account.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={release.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => release.mutate()}
            disabled={release.isPending}
          >
            {release.isPending ? 'Releasing…' : 'Release number'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * AssignNumberDialog — searchable picker over the reseller's UNASSIGNED
 * pool (rule 13: options load from the API, never raw ids). Picking a
 * number and confirming PATCHes customer_id, which stamps assigned_at and
 * derives reseller_id server-side. Routing is intentionally a second step:
 * the freshly-assigned row shows "Not routed" and the pencil opens the
 * routing dialog.
 */
function AssignNumberDialog({
  customerId, resellerId, onClose,
}: {
  customerId: string;
  resellerId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');

  // One fetch of the pool (scoped to the reseller for super_admin; RLS
  // already scopes reseller_owner), filtered client-side as the user
  // types — snappier than a request per keystroke for a pool this size.
  const { data, isLoading, error } = useQuery({
    queryKey: ['dids', 'pool', resellerId ?? 'mine'],
    queryFn: () => dids.list({
      assigned: 'unassigned',
      limit: 200,
      resellerId: resellerId ?? undefined,
    }),
  });
  const pool = data?.rows ?? [];
  const needle = search.replace(/[^0-9+*#]/g, '');
  const visible = needle.length > 0
    ? pool.filter((r) => r.e164.includes(needle))
    : pool;

  const assign = useMutation({
    mutationFn: () => dids.update(selectedId, { customer_id: customerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-dids'] });
      qc.invalidateQueries({ queryKey: ['dids'] });
      toast({
        title: 'Number assigned',
        description: 'Click the pencil on the new number to set where it routes.',
      });
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not assign the number';
      toast({ title: 'Could not assign', description: m, variant: 'destructive' });
    },
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !assign.isPending) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign a number to this account</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="assign-pool-search">Search your pool</Label>
            <Input
              id="assign-pool-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type digits, e.g. 845…"
              className="font-mono"
              autoComplete="off"
            />
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-2">Loading pool…</p>
          ) : error ? (
            <p role="alert" className="text-sm text-destructive py-2">
              {error instanceof Error ? error.message : 'Failed to load the pool.'}
            </p>
          ) : pool.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No unassigned numbers in your pool. Ask the platform owner to
              provision more numbers.
            </p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No pool numbers match that search.
            </p>
          ) : (
            <ul
              role="listbox"
              aria-label="Unassigned numbers"
              className="max-h-56 overflow-y-auto divide-y divide-border rounded-md border border-border"
            >
              {visible.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selectedId === r.id}
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-start transition-colors ${
                      selectedId === r.id
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-secondary/50'
                    }`}
                  >
                    <span className="font-mono text-sm tracking-wide">{formatE164(r.e164)}</span>
                    <CoverageTag coverage={r.coverage} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {assign.isError && (
            <p role="alert" className="text-xs text-destructive">
              {assign.error instanceof Error ? assign.error.message : 'Failed to assign.'}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={assign.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => assign.mutate()}
              disabled={!selectedId || assign.isPending}
            >
              {assign.isPending ? 'Assigning…' : 'Assign number'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * EditDidRoutingDialog — pick which destination an inbound call lands on.
 *
 * Migration 0086 extended the engine to dispatch every routing kind:
 *   ivr        → IVR tree (every role can pick this)
 *   extension  → bridge to a SIP extension
 *   ring_group → fan-out to a ring group
 *   voicemail  → drop into a voicemail box
 *   transfer   → bridge to a SIP URI or E.164
 *
 * Role gating: since 2026-07-02 (Sam) routing edits are super_admin /
 * reseller_owner only — customer-tier roles never open this dialog (the
 * Phone-numbers card is read-only for them, and the backend rejects their
 * PATCHes outright). The internal canPickAnyKind branches are kept as
 * defense-in-depth should the dialog ever be reused elsewhere.
 */
function EditDidRoutingDialog({ row, onClose }: { row: DidRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const user = useCurrentUser();
  const canPickAnyKind =
    user.role === 'super_admin' || user.role === 'reseller_owner';

  const [kind, setKind] = useState<RoutingKind>(row.routing_kind);
  const [targetId, setTargetId] = useState<string>(row.routing_target_id ?? '');
  const [transferUri, setTransferUri] = useState<string>(row.transfer_uri ?? '');

  // Migration 0087 — scheduled routing. Disabled by default; toggling
  // the switch reveals the time-frame picker + off-frame target. The
  // off-frame fields can stay NULL when scheduling is on (graceful
  // fallback: not-in-frame falls through to the primary target).
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(
    Boolean(row.time_frame_id),
  );
  const [timeFrameId, setTimeFrameId] = useState<string>(row.time_frame_id ?? '');
  const [offKind, setOffKind] = useState<RoutingKind>(
    row.off_frame_routing_kind ?? 'ivr',
  );
  const [offTargetId, setOffTargetId] = useState<string>(
    row.off_frame_routing_target_id ?? '',
  );
  const [offTransferUri, setOffTransferUri] = useState<string>(
    row.off_frame_transfer_uri ?? '',
  );

  // Per-kind option lists. Each fires only when its kind is selected
  // (or pre-selected on dialog open) to keep the network footprint
  // small for the common IVR case.
  const { data: treesData, isLoading: treesLoading } = useQuery({
    queryKey: ['ivrTrees', 'picker', row.customer_id],
    // MUST scope to the DID's customer — without the filter a reseller
    // (RLS = all their customers) was offered every account's trees here
    // (Sam 2026-08-06). The backend now also rejects cross-customer
    // targets, but the picker shouldn't offer them in the first place.
    queryFn: () => ivrTrees.list({
      limit: 100,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: kind === 'ivr',
  });
  const trees = treesData?.rows ?? [];

  const { data: extData, isLoading: extLoading } = useQuery({
    queryKey: ['extensions', 'picker', row.customer_id],
    queryFn: () => extensions.list({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: (kind === 'extension' || offKind === 'extension') && canPickAnyKind,
  });
  const exts = extData?.rows ?? [];

  const { data: rgData, isLoading: rgLoading } = useQuery({
    queryKey: ['ringGroups', 'picker', row.customer_id],
    queryFn: () => ringGroups.list({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: (kind === 'ring_group' || offKind === 'ring_group') && canPickAnyKind,
  });
  const groups = rgData?.rows ?? [];

  const { data: vmData, isLoading: vmLoading } = useQuery({
    queryKey: ['voicemailBoxes', 'picker', row.customer_id],
    queryFn: () => voicemails.listBoxes({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: (kind === 'voicemail' || offKind === 'voicemail') && canPickAnyKind,
  });
  const boxes = vmData?.rows ?? [];

  // Time frames list — only fetched when scheduling is enabled, so the
  // common unscheduled DID has zero TF cost.
  const { data: framesData, isLoading: framesLoading } = useQuery({
    queryKey: ['timeFrames', 'picker', row.customer_id],
    queryFn: () => timeFrames.list({
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: scheduleEnabled,
  });
  const frames = framesData?.rows ?? [];

  // The off-frame target pickers reuse the primary fetch hooks above
  // (TanStack dedupes by queryKey); when the user picks 'extension' or
  // 'ring_group' for the off-frame slot, we toggle the same hook by
  // OR-ing the enabled flag. See the data-fetching tweaks above.

  // Reset the target when the kind changes — a UUID picked for one
  // kind is meaningless for another. Keeps the form state coherent.
  useEffect(() => {
    if (kind !== row.routing_kind) {
      setTargetId('');
      if (kind !== 'transfer') setTransferUri('');
    } else {
      setTargetId(row.routing_target_id ?? '');
      setTransferUri(row.transfer_uri ?? '');
    }
  }, [kind, row.routing_kind, row.routing_target_id, row.transfer_uri]);

  // Same self-clearing logic the old IVR-only picker had — if the
  // existing target_id has been soft-deleted, fall back to placeholder.
  useEffect(() => {
    if (kind === 'ivr' && !treesLoading && trees.length > 0 && row.routing_target_id) {
      if (!trees.some((t) => t.id === row.routing_target_id)) setTargetId('');
    }
  }, [kind, treesLoading, trees, row.routing_target_id]);

  const update = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof dids.update>[1] = { routing_kind: kind };
      if (kind === 'transfer') {
        body.transfer_uri = transferUri.trim();
        body.routing_target_id = null;
      } else {
        body.routing_target_id = targetId || null;
        body.transfer_uri = null;
      }
      // Migration 0087 — scheduled routing. Switch off → clear every
      // off-frame field. Switch on → send the off-frame tuple even if
      // some fields are empty (server handles graceful fallback).
      if (scheduleEnabled && timeFrameId) {
        body.time_frame_id = timeFrameId;
        body.off_frame_routing_kind = offKind;
        if (offKind === 'transfer') {
          body.off_frame_transfer_uri = offTransferUri.trim() || null;
          body.off_frame_routing_target_id = null;
        } else {
          body.off_frame_routing_target_id = offTargetId || null;
          body.off_frame_transfer_uri = null;
        }
      } else {
        body.time_frame_id = null;
        body.off_frame_routing_kind = null;
        body.off_frame_routing_target_id = null;
        body.off_frame_transfer_uri = null;
      }
      return dids.update(row.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-dids'] });
      qc.invalidateQueries({ queryKey: ['ivrTree'] });
      toast({ title: 'Routing updated' });
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not save';
      toast({ title: 'Could not save', description: m, variant: 'destructive' });
    },
  });

  function targetReady(): boolean {
    if (kind === 'transfer') return transferUri.trim().length > 0;
    return targetId.length > 0;
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!targetReady()) {
      toast({
        title: kind === 'transfer' ? 'Enter a SIP URI or E.164' : 'Pick a destination',
        variant: 'destructive',
      });
      return;
    }
    update.mutate();
  };

  const dirty =
    kind !== row.routing_kind
    || (kind === 'transfer'
      ? transferUri.trim() !== (row.transfer_uri ?? '')
      : targetId !== (row.routing_target_id ?? ''));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit routing for {formatE164(row.e164)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          {/* Kind picker — collapsed to a hidden 'ivr' for customer-tier
              roles so they can't even see the other options. The
              backend rejects non-IVR kinds from them anyway, so this
              is UX honesty. */}
          {canPickAnyKind ? (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-kind">Destination type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as RoutingKind)}>
                <SelectTrigger id="did-routing-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ivr">IVR tree</SelectItem>
                  <SelectItem value="extension">Extension</SelectItem>
                  <SelectItem value="ring_group">Ring group</SelectItem>
                  <SelectItem value="voicemail">Voicemail box</SelectItem>
                  <SelectItem value="transfer">External transfer (SIP / E.164)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Routes inbound calls to an IVR tree. Other destinations
              (extensions, ring groups, voicemail boxes, external transfers)
              require an admin — ask your reseller if you need one of those.
            </p>
          )}

          {/* Per-kind target picker */}
          {kind === 'ivr' && (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-tree">Route to</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger id="did-routing-tree">
                  <SelectValue placeholder={treesLoading ? 'Loading trees…' : 'Pick an IVR tree…'} />
                </SelectTrigger>
                <SelectContent>
                  {trees.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === 'extension' && canPickAnyKind && (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-ext">Route to</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger id="did-routing-ext">
                  <SelectValue placeholder={extLoading ? 'Loading extensions…' : 'Pick an extension…'} />
                </SelectTrigger>
                <SelectContent>
                  {exts.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      <span className="font-mono mr-2">{e.number}</span>
                      {e.display_name ?? <span className="text-muted-foreground">no name</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === 'ring_group' && canPickAnyKind && (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-rg">Route to</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger id="did-routing-rg">
                  <SelectValue placeholder={rgLoading ? 'Loading ring groups…' : 'Pick a ring group…'} />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === 'voicemail' && canPickAnyKind && (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-vm">Route to</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger id="did-routing-vm">
                  <SelectValue placeholder={vmLoading ? 'Loading voicemail boxes…' : 'Pick a voicemail box…'} />
                </SelectTrigger>
                <SelectContent>
                  {boxes.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === 'transfer' && canPickAnyKind && (
            <div className="space-y-1.5">
              <Label htmlFor="did-routing-xfer">SIP URI or E.164</Label>
              <Input
                id="did-routing-xfer"
                value={transferUri}
                onChange={(e) => setTransferUri(e.target.value)}
                placeholder="sip:reception@office.example.com  or  +15551234567"
                className="font-mono"
              />
              <p className="text-[11px] text-muted-foreground leading-snug">
                Inbound calls are bridged directly to this destination via the
                external SIP profile. Accepts a full SIP URI (sip:user@host),
                an E.164 number (+1…), or a bare bridge target (sofia/…).
              </p>
            </div>
          )}

          {/* Migration 0087 — scheduled routing. Disabled by default.
              When on, the primary tuple above becomes the "in-frame"
              destination and a second tuple here covers "not in frame". */}
          <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Switch
                id="did-schedule-toggle"
                checked={scheduleEnabled}
                onCheckedChange={setScheduleEnabled}
              />
              <span className="text-sm font-medium">Use a time frame for this number</span>
            </label>
            <p className="text-[11px] text-muted-foreground leading-snug">
              When the attached frame says we're in-frame, the primary
              destination above is used. When out-of-frame, the off-hours
              destination below kicks in. Time frames are defined under
              Time Frames in the sidebar.
            </p>

            {scheduleEnabled && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="space-y-1.5">
                  <Label htmlFor="did-schedule-frame">Time frame</Label>
                  <Select value={timeFrameId} onValueChange={setTimeFrameId}>
                    <SelectTrigger id="did-schedule-frame">
                      <SelectValue placeholder={framesLoading ? 'Loading frames…' : 'Pick a frame…'} />
                    </SelectTrigger>
                    <SelectContent>
                      {frames.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          <span className="inline-flex items-center gap-2">
                            <span>{f.name}</span>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {formatTimeFrameRef(f.ref_number)}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="did-offkind">When NOT in frame, route to</Label>
                  <Select value={offKind} onValueChange={(v) => setOffKind(v as RoutingKind)}>
                    <SelectTrigger id="did-offkind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ivr">IVR tree</SelectItem>
                      <SelectItem value="extension">Extension</SelectItem>
                      <SelectItem value="ring_group">Ring group</SelectItem>
                      <SelectItem value="voicemail">Voicemail box</SelectItem>
                      <SelectItem value="transfer">External transfer (SIP / E.164)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Per-kind off-frame target — same shape as the primary
                    pickers above, narrower because the only audience for
                    scheduled routing today is super_admin / reseller_owner
                    (the toggle is shown to all but the option lists fetch
                    behind the canPickAnyKind gate). */}
                {offKind === 'ivr' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="did-off-tree">Off-hours IVR tree</Label>
                    <Select value={offTargetId} onValueChange={setOffTargetId}>
                      <SelectTrigger id="did-off-tree">
                        <SelectValue placeholder="Pick an IVR tree…" />
                      </SelectTrigger>
                      <SelectContent>
                        {trees.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {offKind === 'extension' && canPickAnyKind && (
                  <div className="space-y-1.5">
                    <Label htmlFor="did-off-ext">Off-hours extension</Label>
                    <Select value={offTargetId} onValueChange={setOffTargetId}>
                      <SelectTrigger id="did-off-ext">
                        <SelectValue placeholder="Pick an extension…" />
                      </SelectTrigger>
                      <SelectContent>
                        {exts.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            <span className="font-mono mr-2">{e.number}</span>
                            {e.display_name ?? <span className="text-muted-foreground">no name</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {offKind === 'ring_group' && canPickAnyKind && (
                  <div className="space-y-1.5">
                    <Label htmlFor="did-off-rg">Off-hours ring group</Label>
                    <Select value={offTargetId} onValueChange={setOffTargetId}>
                      <SelectTrigger id="did-off-rg">
                        <SelectValue placeholder="Pick a ring group…" />
                      </SelectTrigger>
                      <SelectContent>
                        {groups.map((g) => (
                          <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {offKind === 'voicemail' && canPickAnyKind && (
                  <div className="space-y-1.5">
                    <Label htmlFor="did-off-vm">Off-hours voicemail box</Label>
                    <Select value={offTargetId} onValueChange={setOffTargetId}>
                      <SelectTrigger id="did-off-vm">
                        <SelectValue placeholder="Pick a voicemail box…" />
                      </SelectTrigger>
                      <SelectContent>
                        {boxes.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {offKind === 'transfer' && canPickAnyKind && (
                  <div className="space-y-1.5">
                    <Label htmlFor="did-off-xfer">Off-hours SIP URI or E.164</Label>
                    <Input
                      id="did-off-xfer"
                      value={offTransferUri}
                      onChange={(e) => setOffTransferUri(e.target.value)}
                      placeholder="sip:nightline@office.example.com or +15551234567"
                      className="font-mono"
                    />
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground leading-snug">
                  If you leave the off-hours destination blank, calls
                  fall back to the primary destination above — useful
                  if you want notifications without breaking routing.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save routing'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RoutingSummary({ row }: { row: DidRow }) {
  // IVR: fetch the tree row so we can show its name. Other kinds:
  // resolve from the per-customer list endpoints (cheaper than per-row
  // GETs and TanStack dedupes across rows in the table).
  const { data: tree } = useQuery({
    queryKey: ['ivrTree', row.routing_target_id],
    queryFn: () => ivrTrees.get(row.routing_target_id!),
    enabled: row.routing_kind === 'ivr' && !!row.routing_target_id,
    staleTime: 60_000,
  });
  const { data: extData } = useQuery({
    queryKey: ['extensions', 'picker', row.customer_id],
    queryFn: () => extensions.list({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: row.routing_kind === 'extension' && !!row.routing_target_id,
    staleTime: 60_000,
  });
  const { data: rgData } = useQuery({
    queryKey: ['ringGroups', 'picker', row.customer_id],
    queryFn: () => ringGroups.list({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: row.routing_kind === 'ring_group' && !!row.routing_target_id,
    staleTime: 60_000,
  });
  const { data: vmData } = useQuery({
    queryKey: ['voicemailBoxes', 'picker', row.customer_id],
    queryFn: () => voicemails.listBoxes({
      limit: 200,
      ...(row.customer_id ? { customer_id: row.customer_id } : {}),
    }),
    enabled: row.routing_kind === 'voicemail' && !!row.routing_target_id,
    staleTime: 60_000,
  });

  // Transfer rows don't have a target_id — they have a transfer_uri.
  if (row.routing_kind === 'transfer') {
    if (!row.transfer_uri) {
      return <div className="text-xs text-muted-foreground mt-0.5">Transfer (URI not set)</div>;
    }
    return (
      <div className="text-xs text-muted-foreground mt-0.5">
        Transfers to <span className="font-mono text-foreground">{row.transfer_uri}</span>
      </div>
    );
  }

  if (!row.routing_target_id) {
    return <div className="text-xs text-muted-foreground mt-0.5">Not routed</div>;
  }
  if (row.routing_kind === 'ivr') {
    return (
      <div className="text-xs text-muted-foreground mt-0.5">
        Routes to IVR · <span className="text-foreground">{tree?.name ?? '…'}</span>
      </div>
    );
  }
  if (row.routing_kind === 'extension') {
    const ext = extData?.rows.find((e) => e.id === row.routing_target_id);
    return (
      <div className="text-xs text-muted-foreground mt-0.5">
        Routes to extension ·{' '}
        <span className="text-foreground font-mono">{ext?.number ?? '…'}</span>
        {ext?.display_name && <span className="ml-1 text-foreground">({ext.display_name})</span>}
      </div>
    );
  }
  if (row.routing_kind === 'ring_group') {
    const rg = rgData?.rows.find((g) => g.id === row.routing_target_id);
    return (
      <div className="text-xs text-muted-foreground mt-0.5">
        Routes to ring group · <span className="text-foreground">{rg?.name ?? '…'}</span>
      </div>
    );
  }
  if (row.routing_kind === 'voicemail') {
    const box = vmData?.rows.find((b) => b.id === row.routing_target_id);
    return (
      <div className="text-xs text-muted-foreground mt-0.5">
        Routes to voicemail · <span className="text-foreground">{box?.name ?? '…'}</span>
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground mt-0.5 capitalize">
      Routes to {row.routing_kind}
    </div>
  );
}

function CoverageTag({ coverage }: { coverage: DidRow['coverage'] }) {
  const labels: Record<string, string> = { us: 'US', ca: 'CA', international: 'Intl' };
  return (
    <span className="inline-flex items-center rounded border border-border bg-secondary/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {labels[coverage] ?? coverage}
    </span>
  );
}
